import { useState } from "react";
import { Shield, X, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button, buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useOnboardingChecklist } from "@/hooks/use-api";
import { useAuth } from "@/hooks/use-auth";

const DISMISSED_KEY = "onboarding_banner_dismissed";

export function OnboardingBanner() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISSED_KEY) === "true",
  );

  const { data, isLoading } = useOnboardingChecklist(user?.tenantId);

  function handleDismiss() {
    sessionStorage.setItem(DISMISSED_KEY, "true");
    setDismissed(true);
  }

  if (dismissed || isLoading || !data) return null;

  const total = data.steps.length;
  const completed = data.steps.filter((s) => s.status === "completed").length;

  if (total === 0 || completed === total) return null;

  const nextStep = data.steps.find((s) => s.status === "pending");
  const progressPct = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <Card className="border-primary/30 bg-primary/5">
      <CardContent className="flex items-center gap-4 p-4">
        <div className="shrink-0 rounded-lg bg-primary/10 p-2">
          <Shield className="h-5 w-5 text-primary" />
        </div>

        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-medium">
              Setup Progress:{" "}
              <span className="text-primary">
                {completed}/{total} complete
              </span>
            </p>
          </div>
          <Progress value={progressPct} className="h-1.5" />
          {nextStep && (
            <p className="text-xs text-muted-foreground">
              Next: {nextStep.name}
            </p>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Link
            to="/setup"
            className={cn(
              buttonVariants({ size: "sm", variant: "default" }),
              "flex items-center gap-1",
            )}
          >
            Continue Setup
            <ChevronRight className="h-3.5 w-3.5" />
          </Link>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-foreground"
            onClick={handleDismiss}
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
